import { type TranscriptCue } from '@bynder-studio/render-core';

/**
 * Parse an SRT timecode string (HH:MM:SS,mmm) into total seconds.
 */
export function parseTimecode(timecode: string): number {
    const [time, ms] = timecode.trim().split(',');
    const [hours, minutes, seconds] = time.split(':').map(Number);

    return hours * 3600 + minutes * 60 + seconds + Number(ms) / 1000;
}

/**
 * Format seconds into an SRT timecode string (HH:MM:SS,mmm).
 */
export function formatTimecode(seconds: number): string {
    const totalMs = Math.round(seconds * 1000);
    const ms = totalMs % 1000;
    const totalSecs = Math.floor(totalMs / 1000);
    const secs = totalSecs % 60;
    const totalMins = Math.floor(totalSecs / 60);
    const mins = totalMins % 60;
    const hours = Math.floor(totalMins / 60);

    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')},${String(ms).padStart(3, '0')}`;
}

/**
 * Format seconds into a short timecode string (MM:SS), optionally with milliseconds.
 */
export function formatShortTimecode(seconds: number, withMilliseconds = false): string {
    const totalMs = Math.max(0, Math.round(seconds * 1000));
    const totalSecs = Math.floor(totalMs / 1000);
    const secs = totalSecs % 60;
    const mins = Math.floor(totalSecs / 60);
    const ms = totalMs % 1000;

    const base = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

    return withMilliseconds ? `${base},${String(ms).padStart(3, '0')}` : base;
}

/**
 * Parse a short timecode string (MM:SS or MM:SS,mmm) into total seconds.
 */
export function parseShortTimecode(timecode: string): number {
    const [minutesPart, secondsPart = '0'] = timecode.trim().split(':');
    const [seconds, milliseconds = '0'] = secondsPart.split(',');
    const minutes = Number(minutesPart);

    return minutes * 60 + Number(seconds) + Number(milliseconds) / 1000;
}

/**
 * Convert frames to seconds at a given frame rate.
 */
export function framesToSeconds(frames: number, frameRate: number): number {
    return frames / frameRate;
}

/**
 * Convert seconds to a frame number at a given frame rate.
 */
function secondsToFrame(seconds: number, frameRate: number): number {
    return Math.round(seconds * frameRate);
}

/**
 * Convert an array of TranscriptCues back into an SRT string.
 */
export function cuesToSrt(cues: TranscriptCue[], frameRate: number): string {
    return cues
        .map((cue, i) => {
            const start = formatTimecode(framesToSeconds(cue.startFrame, frameRate));
            const end = formatTimecode(framesToSeconds(cue.endFrame, frameRate));

            return `${i + 1}\n${start} --> ${end}\n${cue.text}`;
        })
        .join('\n\n');
}

/**
 * Parse an SRT file content string into TranscriptCue[].
 *
 * SRT format:
 *   1
 *   00:00:01,000 --> 00:00:04,000
 *   Hello, welcome to our video!
 *
 *   2
 *   00:00:04,000 --> 00:00:08,000
 *   This is a transcript demo.
 */
export function parseSrt(srtContent: string, frameRate: number): TranscriptCue[] {
    const cues: TranscriptCue[] = [];
    // Split on double newline (handles \r\n and \n)
    const blocks = srtContent.trim().replace(/\r\n/g, '\n').split(/\n\n+/);

    blocks.forEach((block) => {
        const lines = block.split('\n');

        // Keep cues even when text is intentionally empty (index + timecode lines only)
        if (lines.length < 2) {
            return;
        }

        const index = lines[0].trim();
        const timecodeMatch = lines[1].match(/(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/);

        if (!timecodeMatch) {
            return;
        }

        const startSeconds = parseTimecode(timecodeMatch[1]);
        const endSeconds = parseTimecode(timecodeMatch[2]);
        const text = lines.slice(2).join('\n').trim();

        cues.push({
            id: `srt-cue-${index}`,
            startFrame: secondsToFrame(startSeconds, frameRate),
            endFrame: secondsToFrame(endSeconds, frameRate),
            text,
        });
    });

    return cues;
}
